// Main modular form component
import React, { useState } from "react";
import { Box, Button, Grid, Typography, CircularProgress, IconButton } from "@mui/material";
import { AutoSelectField, CheckboxField, DateField, DateRangeField, FileUploader, RadioButtonGroup, SelectField, SignatureField, TextArea, TextInput, TimeField, ToggleSwitch } from "./Accessories";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import MinimizeIcon from "@mui/icons-material/Minimize";

export default function ModularForm({
  sections,
  onSubmit,
  isPopup = false,
  trackUpdates = false,
  enableCollapse = false,
  showControlButtons = false,
}) {
  const [formState, setFormState] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(isPopup);
  const [isMinimized, setIsMinimized] = useState(false);

  // Handle field change
  const handleChange = (name, value) => {
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (trackUpdates) {
      validateField(name, value);
    }
  };

  // Field validation
  const validateField = (name, value) => {
    const section = sections.flatMap((s) => s.fields).find((field) => field.name === name);
    const rules = section?.rules || [];
    let error = "";

    for (const rule of rules) {
      if (rule === "required") {
        if (
          value === null ||
          value === undefined ||
          (typeof value === "string" && value.trim() === "") ||
          (Array.isArray(value) && value.length === 0)
        ) {
          error = `${section.label} is required.`;
          break;
        }
      }
      if (rule === "email" && typeof value === "string" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
        error = `Invalid email address.`;
        break;
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    return error;
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    let hasErrors = false;
    const newErrors = {};

    sections.forEach((section) => {
      section.fields.forEach((field) => {
        const error = validateField(field.name, formState[field.name]);
        if (error) hasErrors = true;
        if (error) newErrors[field.name] = error;
      });
    });

    setErrors(newErrors);

    if (!hasErrors) {
      setLoading(true);
      const success = await onSubmit(formState);
      setLoading(false);
      if (success && isPopup) {
        setFormState({});
      }
    }
  };

  return (
    <Box
      sx={{
        padding: 2,
        maxWidth: isFullScreen ? "100%" : isPopup ? 600 : "100%",
        margin: "auto",
        display: isMinimized ? "none" : "block",
        border: isPopup ? "1px solid #ccc" : "none",
        borderRadius: "8px",
        boxShadow: isPopup ? "0px 4px 6px rgba(0, 0, 0, 0.1)" : "none",
      }}
    >
      {showControlButtons && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <IconButton onClick={() => setIsFullScreen((prev) => !prev)}>
            {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton onClick={() => setIsMinimized((prev) => !prev)}>
            <MinimizeIcon />
          </IconButton>
        </Box>
      )}
      <form onSubmit={handleSubmit}>
        {sections.map((section, index) => (
          <Box key={index} mb={4}>
            <Typography variant="h6" gutterBottom>
              {section.title}
            </Typography>
            <Grid container spacing={2}>
              {section.fields.map((field, idx) => {
                const FieldComponent = {
                  text: TextInput,
                  textarea: TextArea,
                  select: SelectField,
                  autoselect: AutoSelectField,
                  date: DateField,
                  time: TimeField,
                  daterange: DateRangeField,
                  file: FileUploader,
                  signature: SignatureField,
                  radio: RadioButtonGroup,
                  toggle: ToggleSwitch,
                  checkbox: CheckboxField,
                }[field.type];

                return (
                  <Grid item xs={12} sm={field.gridSize || 6} key={idx}>
                    <FieldComponent
                      {...field}
                      value={formState[field.name] || ""}
                      onChange={(value) => handleChange(field.name, value)}
                      error={errors[field.name]}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
        <Box mt={4} textAlign="right">
          <Button type="submit" variant="contained" color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Submit"}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
